import { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { Navigate, useNavigate } from 'react-router-dom';

interface TeamMember {
  username: string;   // Cognito Username (the unique identifier)
  email: string;
  name?: string;      // Single name field returned by Cognito
  given_name?: string;
  family_name?: string;
  role: string;
  status: string;
  tenantId?: string;
  subscriptionTier?: string;
}

interface TenantInfo {
  tenantId: string;
  email?: string;
  subscriptionTier?: string;
  propertyCount?: number;
  trialEndsAt?: string;
}

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<TeamMember[]>([]);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<TeamMember | null>(null);
  const [formData, setFormData] = useState({ email: '', password: '', role: 'User', name: '' });
  const [showPassword, setShowPassword] = useState(false);

  const idToken = localStorage.getItem('idToken');
  const accessToken = localStorage.getItem('accessToken') || idToken;

  // Role check: mirrors App.tsx multi-source logic — check custom:role, cognito:groups, AND localStorage
  const decoded: any = idToken ? jwtDecode(idToken) : {};
  const userRole = (decoded['custom:role'] || decoded.role || '').toUpperCase();
  const groups: string[] = Array.isArray(decoded['cognito:groups']) ? decoded['cognito:groups'] : [];
  const isSuperAdmin =
    userRole === 'SUPER_ADMIN' ||
    // Group spellings differ per pool ("SuperAdmins" vs "Super-Admin") —
    // normalise before comparing, same as buildUserFromToken.
    groups.some((g: string) => {
      if (typeof g !== 'string') return false;
      const n = g.toLowerCase().replace(/[^a-z0-9]/g, '');
      return n === 'superadmin' || n === 'superadmins';
    }) ||
    localStorage.getItem('isSuperAdmin') === 'true';
  const isTenantAdmin = userRole === 'TENANT_ADMIN' || userRole === 'ADMIN' || isSuperAdmin;

  const params = new URLSearchParams(window.location.search);
  const tenantId = params.get('tenant_id') || '';

  const API = process.env.REACT_APP_API_URL || '';

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = tenantId
        ? `${API}/api/super-admin/users?tenant_id=${tenantId}`
        : `${API}/api/super-admin/users`;

      const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
      const result = await res.json();

      if (result.success && result.data) {
        setUsers(result.data);
      } else if (Array.isArray(result)) {
        setUsers(result);
      } else {
        setUsers([]);
      }

      // Set tenant info from context if tenantId provided
      if (tenantId) {
        setTenantInfo({ tenantId });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [tenantId]);

  // Redirect if not admin — checked after hooks to comply with React rules
  if (!isSuperAdmin && !isTenantAdmin) {
    console.warn('[AdminUsers] Access denied. Role:', userRole, 'Groups:', groups);
    return <Navigate to="/dashboard" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing tenant user (name + role)
        // Use /api/admin/users endpoint for tenant-level role updates
        const updateRes = await fetch(`${API}/api/admin/users/${editingUser.username}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            role: formData.role, // Already capitalized from dropdown
            tenant_id: tenantId
          })
        });

        if (!updateRes.ok) {
          throw new Error(`Failed to update user: ${updateRes.statusText}`);
        }

        alert('User role updated successfully');
      } else {
        // Create new user
        await fetch(`${API}/api/admin/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            role: formData.role, // Already capitalized from dropdown
            tenant_id: tenantId,
            name: formData.name
          })
        });
      }
      fetchUsers();
      setShowForm(false);
      setEditingUser(null);
      setFormData({ email: '', password: '', role: 'User', name: '' });
    } catch (err: any) {
      alert('Failed to save user: ' + err.message);
    }
  };

  const handleDelete = async (username: string, email: string) => {
    if (!window.confirm(`Remove ${email} from this account?`)) return;
    try {
      await fetch(`${API}/api/super-admin/users/${username}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      fetchUsers();
    } catch (err: any) {
      alert('Failed to remove user: ' + err.message);
    }
  };

  const tierColor = (tier?: string) => {
    if (!tier) return 'bg-gray-100 text-gray-500';
    const t = tier.toLowerCase();
    if (t.includes('pro2') || t.includes('enterprise')) return 'bg-purple-100 text-purple-700';
    if (t.includes('pro1')) return 'bg-blue-100 text-blue-700';
    return 'bg-indigo-100 text-indigo-600';
  };

  const roleColor = (role: string) => {
    const r = (role || '').toLowerCase();
    if (r.includes('admin')) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/super-admin')}
              className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-sm font-medium transition"
            >
              <i className="fas fa-arrow-left mr-2"></i>Back to Super Admin
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <i className="fas fa-users-cog text-emerald-600"></i>
                Manage Account Users
              </h1>
              {tenantId && (
                <p className="text-xs text-gray-500 font-mono mt-0.5">{tenantId}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {tenantInfo?.subscriptionTier && (
              <span className={`px-3 py-1 text-xs font-bold rounded-full ${tierColor(tenantInfo.subscriptionTier)}`}>
                {tenantInfo.subscriptionTier.replace(/_/g, ' ')}
              </span>
            )}
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition shadow-sm"
            >
              <i className="fas fa-user-plus mr-2"></i>Invite User
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-500 border-t-transparent mb-4"></div>
            <p className="text-gray-500 text-sm">Loading account users...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <i className="fas fa-exclamation-circle text-red-400 text-3xl mb-3"></i>
            <p className="text-red-700 font-medium">{error}</p>
            <button onClick={fetchUsers} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition">
              Retry
            </button>
          </div>
        ) : (
          <div className="bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-600">
                {users.length} user{users.length !== 1 ? 's' : ''} on this account
              </span>
            </div>
            {users.length === 0 ? (
              <div className="py-16 text-center text-gray-400">
                <i className="fas fa-users text-4xl mb-3"></i>
                <p className="font-medium">No sub-users found for this tenant.</p>
                <p className="text-sm mt-1">Only the account owner exists.</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map(u => (
                    <tr key={u.username} className="hover:bg-emerald-50/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-bold text-sm">
                            {(u.name || u.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {u.name || `${u.given_name || ''} ${u.family_name || ''}`.trim() || '—'}
                            </div>
                            <div className="text-xs text-gray-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-bold rounded-md ${roleColor(u.role)}`}>
                          {(u.role || 'user').replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-bold rounded-md ${u.status === 'CONFIRMED' ? 'bg-emerald-100 text-emerald-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {u.status || 'PENDING'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button
                          onClick={() => {
                            setEditingUser(u);
                            setFormData({ email: u.email, password: '', role: u.role, name: u.name || `${u.given_name || ''} ${u.family_name || ''}`.trim() });
                            setShowForm(true);
                          }}
                          className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-gray-600 hover:bg-gray-200 text-sm font-bold transition"
                          title="Edit User"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(u.username, u.email)}
                          className="inline-flex items-center px-3 py-1 bg-gray-100 rounded text-red-500 hover:bg-red-50 text-sm font-bold transition"
                          title="Remove User"
                        >
                          <i className="fas fa-user-minus"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Add/Edit User Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">
                  {editingUser ? 'Edit User' : 'Invite User'}
                </h3>
                <button onClick={() => { setShowForm(false); setEditingUser(null); }} className="text-gray-400 hover:text-gray-600">
                  <i className="fas fa-times text-lg"></i>
                </button>
              </div>
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
                  <input
                    type="email"
                    placeholder="user@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                {!editingUser && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Temporary Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min 8 characters"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 pr-16"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name</label>
                  <input
                    type="text"
                    placeholder="Jane Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="User">User</option>
                    <option value="Lead">Lead</option>
                    <option value="Tenant Admin">Tenant Admin</option>
                    <option value="Super Admin">Super Admin</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition"
                  >
                    {editingUser ? 'Save Changes' : 'Create User'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingUser(null); }}
                    className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-bold hover:bg-gray-200 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
